import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Users, Shield, Key, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const UserManagement = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/documentation')} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Docs
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-900">User Management</h1>
          </div>
          <p className="text-lg text-slate-600">Control who has access to your CRM and manage their permissions.</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                User Roles & Permissions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700 mb-4">I-Match CRM has three primary user roles:</p>
              
              <div className="space-y-4">
                <div className="border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-100 text-blue-700">Admin</Badge>
                    <span className="font-semibold text-slate-900">Full Control</span>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700 ml-4">
                    <li>Manage all users and permissions</li>
                    <li>Access admin panel and settings</li>
                    <li>View audit logs</li>
                    <li>Configure integrations and AI settings</li>
                    <li>Full access to all CRM features</li>
                  </ul>
                </div>

                <div className="border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-green-100 text-green-700">Regular User</Badge>
                    <span className="font-semibold text-slate-900">Standard Access</span>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700 ml-4">
                    <li>Create and manage leads</li>
                    <li>Access companies and contacts</li>
                    <li>Use AI enrichment features</li>
                    <li>Manage own tasks and calendar</li>
                    <li>Cannot modify system settings</li>
                  </ul>
                </div>

                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-slate-100 text-slate-700">Export User</Badge>
                    <span className="font-semibold text-slate-900">Data Export Permission</span>
                  </div>
                  <p className="text-sm text-slate-700">A special permission that can be granted to any user to allow CSV exports of lead data.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-600" />
                Adding New Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-3 text-slate-700">
                <li>Navigate to the Admin Panel</li>
                <li>Click the "Users" tab</li>
                <li>Click "Create New User" button</li>
                <li>Fill in user details:
                  <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                    <li>Email address (required)</li>
                    <li>First and last name</li>
                    <li>Password (or send invitation email)</li>
                    <li>Role selection</li>
                  </ul>
                </li>
                <li>Click "Create User"</li>
              </ol>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-slate-700">
                  <strong>Note:</strong> New users will receive an email with login instructions if you select the "Send Invitation" option.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-orange-600" />
                Managing Existing Users
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">As an admin, you can:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
                <li><strong>Deactivate users:</strong> Temporarily suspend access without deleting the account</li>
                <li><strong>Reset passwords:</strong> Send password reset links to users who are locked out</li>
                <li><strong>Change roles:</strong> Promote users to admin or adjust their permissions</li>
                <li><strong>Delete users:</strong> Permanently remove users from the system</li>
                <li><strong>Impersonate users:</strong> Log in as another user to troubleshoot issues (admin only)</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Best Practices</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Limit admin access to only trusted team members</li>
                <li>Regularly review the user list and deactivate accounts no longer needed</li>
                <li>Use strong passwords and enable MFA when available</li>
                <li>Monitor audit logs for unusual activity</li>
                <li>Only grant export permissions to users who genuinely need it</li>
              </ul>
            </CardContent>
          </Card>

          <div className="flex gap-3 pt-6">
            <Button onClick={() => navigate('/documentation')} className="bg-indigo-600 hover:bg-indigo-700">
              Browse All Documentation
            </Button>
            <Button variant="outline" onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
